import type { PersonalSignalContext } from './personalSignalContext';
import type {
  PersonalDashboardFacts,
  PersonalOperatorFacts,
  PersonalSignal,
  PersonalSignalSeverity,
} from './reportsPersonalTypes';
import {
  evaluateVoidedSalesBranch,
  evaluateNoCashierBranch,
  evaluateHighCustomItemsBranch,
  evaluateHighCustomItemsOperator,
  evaluateCashShortageOperator,
  evaluateCashierMismatchOperator,
  evaluateLowTicketVsTeamOperator,
  evaluateCashGapOperator,
  evaluatePendingPaymentsOperator,
  evaluateTicketBelowOwnHistoryOperator,
  evaluateStockNoSalesOperator,
  evaluateHighShrinkageOperator,
  evaluateHighCustomerBalanceOperator,
  evaluateHighVoidedSalesOperator,
} from './personalSignalEvaluators';

export type PersonalSignalDefinition = {
  code: string;
  severity: PersonalSignalSeverity;
  requiresTeamComparison: boolean;
  evaluateBranch?: (facts: PersonalDashboardFacts, ctx: PersonalSignalContext) => PersonalSignal[];
  evaluateOperator?: (
    op: PersonalOperatorFacts,
    facts: PersonalDashboardFacts,
    ctx: PersonalSignalContext
  ) => PersonalSignal[];
};

export const PERSONAL_SIGNALS: PersonalSignalDefinition[] = [
  {
    code: 'voided_sales',
    severity: 'info',
    requiresTeamComparison: false,
    evaluateBranch: evaluateVoidedSalesBranch,
  },
  {
    code: 'no_cashier',
    severity: 'review',
    requiresTeamComparison: false,
    evaluateBranch: evaluateNoCashierBranch,
  },
  {
    code: 'high_custom_items',
    severity: 'review',
    requiresTeamComparison: false,
    evaluateBranch: evaluateHighCustomItemsBranch,
    evaluateOperator: evaluateHighCustomItemsOperator,
  },
  {
    code: 'cash_shortage',
    severity: 'priority',
    requiresTeamComparison: false,
    evaluateOperator: evaluateCashShortageOperator,
  },
  {
    code: 'cashier_mismatch',
    severity: 'review',
    requiresTeamComparison: true,
    evaluateOperator: evaluateCashierMismatchOperator,
  },
  {
    code: 'low_ticket_vs_team',
    severity: 'review',
    requiresTeamComparison: true,
    evaluateOperator: evaluateLowTicketVsTeamOperator,
  },
  {
    code: 'cash_gap',
    severity: 'review',
    requiresTeamComparison: false,
    evaluateOperator: evaluateCashGapOperator,
  },
  {
    code: 'pending_payments',
    severity: 'review',
    requiresTeamComparison: true,
    evaluateOperator: evaluatePendingPaymentsOperator,
  },
  {
    code: 'ticket_below_own_history',
    severity: 'review',
    requiresTeamComparison: false,
    evaluateOperator: evaluateTicketBelowOwnHistoryOperator,
  },
  {
    code: 'stock_no_sales',
    severity: 'review',
    requiresTeamComparison: false,
    evaluateOperator: evaluateStockNoSalesOperator,
  },
  {
    code: 'high_shrinkage',
    severity: 'review',
    requiresTeamComparison: true,
    evaluateOperator: evaluateHighShrinkageOperator,
  },
  {
    code: 'high_fiado_balance',
    severity: 'review',
    requiresTeamComparison: true,
    evaluateOperator: evaluateHighCustomerBalanceOperator,
  },
  {
    code: 'high_voided_sales',
    severity: 'review',
    requiresTeamComparison: false,
    evaluateOperator: evaluateHighVoidedSalesOperator,
  },
];

export function evaluatePersonalSignals(
  facts: PersonalDashboardFacts,
  ctx: PersonalSignalContext
): { branchSignals: PersonalSignal[]; operatorSignals: Map<string, PersonalSignal[]> } {
  const branchSignals: PersonalSignal[] = [];
  const operatorSignals = new Map<string, PersonalSignal[]>();

  for (const def of PERSONAL_SIGNALS) {
    if (def.requiresTeamComparison && ctx.teamMode === 'solo') continue;

    if (def.evaluateBranch) {
      branchSignals.push(...def.evaluateBranch(facts, ctx));
    }
    if (def.evaluateOperator) {
      for (const op of facts.operators) {
        const key = op.cashierUserId ?? '__no_cashier__';
        const list = def.evaluateOperator(op, facts, ctx);
        if (list.length === 0) continue;
        const existing = operatorSignals.get(key) ?? [];
        operatorSignals.set(key, [...existing, ...list]);
      }
    }
  }

  return { branchSignals, operatorSignals };
}

export function flattenPersonalSignals(
  branchSignals: PersonalSignal[],
  operatorSignals: Map<string, PersonalSignal[]>
): PersonalSignal[] {
  const out = [...branchSignals];
  for (const list of operatorSignals.values()) {
    out.push(...list);
  }
  return out;
}
